"use client";

import { useId, useRef, useState, type ReactNode } from "react";
import styles from "./Tabs.module.css";

/*
  Replaces the Webflow `w-tabs` widget used by the "Why Quantstamp" section on
  the homepage and the audits page. Both pages render this on desktop and an
  <AccordionGroup> of the same content on mobile, which is how the original was
  built too.

  Arrow-key navigation and the roving tabindex are part of the tab pattern and
  were not in the Webflow widget; the previous imperative shim added them back,
  and keeping them here means they come free at every call site.
*/

export type TabItem = {
  id: string;
  label: ReactNode;
  content: ReactNode;
};

export function Tabs({
  items,
  className,
  tabListClassName,
  tabClassName,
  panelClassName,
  label,
}: {
  items: TabItem[];
  className?: string;
  tabListClassName?: string;
  tabClassName?: string;
  panelClassName?: string;
  /* Names the tablist for screen readers, e.g. "Why Quantstamp". */
  label: string;
}) {
  const baseId = useId();
  const [active, setActive] = useState(0);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  function onKeyDown(event: React.KeyboardEvent<HTMLDivElement>) {
    const keys: Record<string, number> = {
      ArrowRight: 1,
      ArrowDown: 1,
      ArrowLeft: -1,
      ArrowUp: -1,
    };
    let next: number | null = null;
    if (event.key in keys) next = (active + keys[event.key] + items.length) % items.length;
    else if (event.key === "Home") next = 0;
    else if (event.key === "End") next = items.length - 1;
    if (next === null) return;
    event.preventDefault();
    setActive(next);
    tabRefs.current[next]?.focus();
  }

  return (
    <div className={[styles.root, className].filter(Boolean).join(" ")}>
      <div
        role="tablist"
        aria-label={label}
        className={[styles.tabList, tabListClassName].filter(Boolean).join(" ")}
        onKeyDown={onKeyDown}
      >
        {items.map((item, index) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            id={`${baseId}-tab-${item.id}`}
            ref={(node) => {
              tabRefs.current[index] = node;
            }}
            className={[styles.tab, tabClassName].filter(Boolean).join(" ")}
            aria-selected={index === active}
            aria-controls={`${baseId}-panel-${item.id}`}
            /* Only the active tab is a tab stop; the arrow keys move between them. */
            tabIndex={index === active ? 0 : -1}
            onClick={() => setActive(index)}
          >
            {item.label}
          </button>
        ))}
      </div>
      {items.map((item, index) => (
        <div
          key={item.id}
          role="tabpanel"
          id={`${baseId}-panel-${item.id}`}
          aria-labelledby={`${baseId}-tab-${item.id}`}
          className={[styles.panel, panelClassName].filter(Boolean).join(" ")}
          hidden={index !== active}
          tabIndex={0}
        >
          {item.content}
        </div>
      ))}
    </div>
  );
}
